import { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
  FormControl,
  Fade,
} from "@mui/material";
import Swal from "sweetalert2";

import api from "../../../api/axios";
import { useNavigate } from "react-router-dom";
import {
  CommonSubHeader,
  useLocationHelpers,
  NavButton,
} from "../../../Components/helpers/Helpers";

import {
  useRichTextEditor,
  RichTextEditor,
} from "../../../Mensajes/components/Editor";
import LoadingPage from "../../../Components/Common/Loading/LoadingPage";

const Crear = () => {
  const navigate = useNavigate();
  const { goPanel, goTable, buildTableUrl } = useLocationHelpers();

  /* ===========================
      RICH TEXT EDITOR
  ============================ */

  const { editorState, onEditorChange, clearEditor, getHtmlFromEditor } =
    useRichTextEditor();

  /* ===========================
      STATE
  ============================ */

  const [loadingOptions, setLoadingOptions] = useState(false);

  const [options, setOptions] = useState({
    companies: [],
  });

  const [contractData, setContractData] = useState({
    company_id: 0,
    contract_title: "",
    contract: "",
    contract_contatcs: "",
    contract_price: "",
    contract_percentage: "",
    contract_files: "",
  });

  const [marketingToSend, setMarketingToSend] = useState(0);

  /* ===========================
      FETCH INITIAL DATA
  ============================ */

  const getData = async () => {
    try {
      setLoadingOptions(true);

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/create`,
      );

      console.log(data);

      setOptions({
        companies: [{ company_name: "Seleccionar", id: 0 }, ...data.companies],
      });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudieron cargar los datos", "error");
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  /* ===========================
      HANDLERS
  ============================ */

  const handleChange = ({ target }) => {
    setContractData((prev) => ({
      ...prev,
      [target.name]: target.value.trim(),
    }));
  };

  const handleAutocompleteChange = (_, value, fieldName) => {
    setContractData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  /* ===========================
      FORM CONFIG
  ============================ */

  const formFields = [
    {
      label: "Empresa",
      name: "company_id",
      type: "autocomplete",
      options: options.companies,
      getOptionLabel: (o) => o.company_name || "",
      isOptionEqualToValue: (o, v) => o.id === v.id,
    },
    {
      label: "Hoja de encargo/Contrato",
      name: "contract_title",
      type: "text",
    },
    {
      label: "Observaciones",
      name: "contract",
      type: "editor",
      required: false,
    },
  ];

  /* ===========================
      VALIDATION
  ============================ */

  const fieldLabelMap = Object.fromEntries(
    formFields.map((f) => [f.name, f.label]),
  );

  const requiredFields = formFields
    .filter((f) => f.required !== false)
    .map((f) => f.name);

  const validateFields = () =>
    requiredFields.every(
      (field) => contractData[field] !== "" && contractData[field] !== null,
    );

  const showValidationErrors = () => {
    const missingFields = requiredFields
      .filter(
        (field) => contractData[field] === "" || contractData[field] === null,
      )
      .map((field) => `<li>${fieldLabelMap[field]}</li>`)
      .join("");

    Swal.fire({
      icon: "error",
      title: "Campos obligatorios",
      html: `<div style="text-align:left">
        Complete los siguientes campos:
        <ul>${missingFields}</ul>
      </div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  const buildPayload = () => {
    return {
      ...contractData,
      company_id: contractData.company_id?.id || 0,
      contract: getHtmlFromEditor(),
    };
  };

  const validateForm = () => {
    const errors = [];

    if (!contractData.company_id || contractData.company_id.id === 0) {
      errors.push("Empresa");
    }

    if (!contractData.contract_title.trim()) {
      errors.push("Hoja de encargo/Contrato");
    }

    const html = getHtmlFromEditor();
    const isEmpty = !html || html.replace(/<[^>]+>/g, "").trim() === "";

    if (isEmpty) {
      errors.push("Observaciones");
    }

    if (errors.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Campos obligatorios",
        html: `
        <div style="text-align:left">
          Complete los siguientes campos:
          <ul>
            ${errors.map((e) => `<li>${e}</li>`).join("")}
          </ul>
        </div>
      `,
        confirmButtonColor: "#1ab394",
      });

      return false;
    }

    return true;
  };

  /* ===========================
      SUBMIT
  ============================ */

  const handleContractsFormInformation = async () => {
    if (!validateForm()) return;

    const confirm = await Swal.fire({
      title: "¿Desea guardar los cambios?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Guardar",
      cancelButtonText: "Volver",
      confirmButtonColor: "#1ab394",
      reverseButtons: true,
    });

    if (!confirm.isConfirmed) return;

    try {
      Swal.fire({
        title: "Guardando Hoja de encargo/Contrato",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/contracts/store`,
        buildPayload(),
      );

      Swal.close();
      clearEditor();

      const contractId = data?.contract_created?.id;

      await Swal.fire({
        icon: "success",
        title: "Hoja de encargo/Contrato creado",
        confirmButtonColor: "#1ab394",
      });

      if (contractId) {
        navigate(`/contratos/ver?id=${contractId}`);
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "No se pudo guardar la información", "error");
    }
  };

  /* ===========================
      RENDER
  ============================ */

  return (
    <div className="fatherContainer">
      <CommonSubHeader
        title="Hojas de encargo - Contratos"
        subTitle="Crear"
        specialRute="contratos"
      />
      {loadingOptions && <LoadingPage isLoading />}
      {!loadingOptions && (
        <Fade in timeout={800}>
          <Box component="form" className="formMainContainer" noValidate>
            <Grid container p={2}>
              {formFields.map((field, idx) =>
                field.type === "autocomplete" ? (
                  <Grid item xs={12} sm={6} key={idx} p={1}>
                    <Autocomplete
                      value={contractData[field.name]}
                      options={field.options}
                      loading={loadingOptions}
                      size="small"
                      getOptionLabel={field.getOptionLabel}
                      isOptionEqualToValue={field.isOptionEqualToValue}
                      onChange={(e, v) =>
                        handleAutocompleteChange(e, v, field.name)
                      }
                      renderInput={(params) => (
                        <TextField {...params} label={field.label} fullWidth />
                      )}
                    />
                  </Grid>
                ) : field.type === "editor" ? (
                  <Grid item xs={12} key={idx} p={1}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ my: 2 }}>{field.label}</FormLabel>

                      <RichTextEditor
                        editorState={editorState}
                        onEditorChange={onEditorChange}
                      />
                    </FormControl>
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} key={idx} p={1}>
                    <TextField
                      name={field.name}
                      label={field.label}
                      value={contractData[field.name]}
                      onChange={handleChange}
                      size="small"
                      fullWidth
                    />
                  </Grid>
                ),
              )}

              <Grid item xs={12} mt={2}>
                <ButtonGroup fullWidth>
                  <Button
                    color="success"
                    onClick={handleContractsFormInformation}>
                    Guardar
                  </Button>
                  <NavButton
                    color="error"
                    to={buildTableUrl("contratos")}
                    onClick={() => goTable("contratos")}>
                    Tabla
                  </NavButton>
                  <Button onClick={goPanel}>Inicio</Button>
                </ButtonGroup>
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}
    </div>
  );
};

export default Crear;
